import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { api } from '../App';

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const expired = new URLSearchParams(window.location.search).get('expired') === '1';

  useEffect(() => {
    // Stale token from a previous day: make sure nothing "half logged-in" remains.
    if (expired) {
      localStorage.removeItem('gs_admin_token');
      localStorage.removeItem('gs_admin_name');
      localStorage.removeItem('gs_admin_role');
    }
  }, [expired]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please enter your email address and password'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/admin/login', { email, password });
      localStorage.setItem('gs_admin_token', res.token);
      localStorage.setItem('gs_admin_name', res.admin?.name || 'Staff');
      localStorage.setItem('gs_admin_role', res.admin?.role || 'admin');
      toast.success(`Welcome back, ${res.admin?.name || ''}!`);
      // Preserve deep-link tab (e.g. PWA scanner start URL: /admin?tab=scanner)
      const params = new URLSearchParams(window.location.search);
      params.delete('expired');
      const qs = params.toString();
      navigate('/admin' + (qs ? `?${qs}` : ''));
    } catch (e) {
      toast.error(e.message || 'Invalid credentials');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] px-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center">
          <div className="w-12 h-12 rounded-xl bg-[hsl(var(--gs-navy))] flex items-center justify-center mx-auto mb-3 text-white"><Shield className="w-6 h-6" /></div>
          <CardTitle className="text-2xl" style={{fontFamily: 'Space Grotesk'}}>Staff Login</CardTitle>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Admin · Treasurer · Chairman · Committee</p>
        </CardHeader>
        <CardContent>
          {expired && (
            <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status" data-testid="admin-login-session-expired">
              Your staff session expired (sessions last 3 days). Please sign in again to continue.
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <div><Label>Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@gujaratsamajnottingham.com" data-testid="admin-email-input" /></div>
            <div>
              <Label>Password</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-[hsl(var(--gs-navy))] text-white hover:bg-[hsl(var(--gs-navy-2))] h-11" data-testid="admin-login-button">
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          <p className="text-xs text-center text-[hsl(var(--muted-foreground))] mt-4">Staff access only — contact the admin for an account</p>
        </CardContent>
      </Card>
    </div>
  );
}
